import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldCheck, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { authAPI } from '../services/api';

const ParentalConsent = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [childName, setChildName] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No consent token provided. Please use the link from your email.');
      return;
    }

    const approveConsent = async () => {
      try {
        const res = await authAPI.parentalConsent(token);
        if (res.data.success) {
          setStatus('success');
          setMessage(res.data.message);
          setChildName(res.data.childName || '');
        } else {
          setStatus('error');
          setMessage(res.data.message);
        }
      } catch {
        setStatus('error');
        setMessage('Something went wrong. Please try again or contact support.');
      }
    };

    approveConsent();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md shadow-lg" data-testid="parental-consent-card">
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-white rounded-full p-4 shadow-md">
              <img src="/logo.png" alt="SportiZn" className="h-14 w-auto" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Parental Consent</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-8" data-testid="consent-loading">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <p className="text-muted-foreground">Processing consent...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center gap-4 py-4" data-testid="consent-success">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-foreground">Consent Approved</h3>
                <p className="text-muted-foreground">{message}</p>
              </div>
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 w-full">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-foreground">
                    <p className="font-medium">Safety measures in place:</p>
                    <ul className="mt-1 space-y-1 text-muted-foreground">
                      <li>Profile is set to Private by default</li>
                      <li>Only connections can send direct messages</li>
                      <li>Privacy settings are locked for safety</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4" data-testid="consent-error">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold text-foreground">Consent Failed</h3>
                <p className="text-muted-foreground">{message}</p>
              </div>
            </div>
          )}

          <Link to="/login">
            <Button variant="outline" className="w-full mt-4" data-testid="consent-login-btn">
              Go to SportiZn
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentalConsent;
